import { hasStraightLaser, type ContractLaser, type LaseredClaimantListItem, type LaseredCondition } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { featureFlags } from "@/utils/featureFlags";

/**
 * The three laser types (PO 2026-10-07). A lasered claimant is exactly one:
 * - `standard`: one higher deductible (a straight laser). A legacy
 *   `condition_based` straight laser is standard too: its codes are reference only.
 * - `contract`: a contract laser or a full exclusion from the contract.
 * - `conditional`: one or more conditional lasers, with an optional standing
 *   deductible (a straight laser under them); without one the policy base applies.
 */
export type LaserKind = "standard" | "contract" | "conditional";

export const LASER_KIND_LABELS: Record<LaserKind, string> = {
  standard: "Standard",
  contract: "Contract",
  conditional: "Conditional",
};

/** The type cards / options, same wording wherever a laser is added. */
export const LASER_KIND_OPTIONS: { value: LaserKind; title: string; description: string }[] = [
  {
    value: "standard",
    title: LASER_KIND_LABELS.standard,
    description: "One higher deductible for all of this claimant's claims.",
  },
  {
    value: "contract",
    title: LASER_KIND_LABELS.contract,
    description: "An elevated deductible for a contract period, or a full exclusion from the contract.",
  },
  {
    value: "conditional",
    title: LASER_KIND_LABELS.conditional,
    description:
      "Deductibles that apply when a claim matches a condition's codes, with an optional lasered specific deductible.",
  },
];

/**
 * The type options on claimant create and policy create: Conditional only when
 * the environment's API has the conditional-laser routes (featureFlags).
 */
export const createLaserKindOptions = (conditionalEnabled: boolean = featureFlags.conditionalLasersEnabled) =>
  LASER_KIND_OPTIONS.filter((o) => o.value !== "conditional" || conditionalEnabled);

/** Under the Conditional standing-deductible field. */
export const STANDING_DEDUCTIBLE_HINT =
  "Applies to all claims until a conditional laser triggers. Leave it blank to use the policy base deductible.";

/** A contract laser or full exclusion is set. */
export const hasContractLaser = (
  cl?: Pick<ContractLaser, "is_contract_lasered" | "is_contract_excluded"> | null
): boolean => !!(cl?.is_contract_lasered || cl?.is_contract_excluded);

type LaserKindRow = Pick<
  LaseredClaimantListItem,
  "is_lasered" | "laser_type" | "lasered_conditions" | "contract_laser"
>;

/**
 * The row's laser type: contract, then conditional (any live conditional laser),
 * then standard. Legacy data can carry more than one; this precedence is the
 * deductible's (contract terms win, conditional lasers sit above a straight one).
 */
export const laserKindOf = (row: LaserKindRow): LaserKind | undefined => {
  if (hasContractLaser(row.contract_laser)) return "contract";
  if ((row.lasered_conditions?.length ?? 0) > 0) return "conditional";
  if (hasStraightLaser(row)) return "standard";
  return undefined;
};

/** Standard, Contract, Excluded or Conditional; "" when the row has no laser. */
export const laserKindLabel = (row: LaserKindRow): string => {
  const kind = laserKindOf(row);
  if (!kind) return "";
  if (kind === "contract" && row.contract_laser?.is_contract_excluded) return "Excluded";
  return LASER_KIND_LABELS[kind];
};

// ─────────────────────────────────────────────────────────────
// What a save removes (laser dialog)
// ─────────────────────────────────────────────────────────────

/** A claimant's laser data on one policy before a save. */
export interface ExistingLaser {
  /** The straight laser's deductible; undefined when there is no straight laser. */
  straightAmount?: number;
  conditions: Pick<LaseredCondition, "lasered_condition_name">[];
  /** Set only when a contract laser or exclusion is on file. */
  contract?: Pick<ContractLaser, "is_contract_lasered" | "is_contract_excluded" | "amt_contract_lasered_deductible">;
}

export interface LaserClearPlan {
  straight: boolean;
  conditions: boolean;
  contract: boolean;
}

export const NO_LASER_CLEAR: LaserClearPlan = { straight: false, conditions: false, contract: false };

/**
 * What saving as `kind` removes, so the claimant ends up as that one type.
 * - Standard keeps the straight laser and removes conditional lasers and a contract laser.
 * - Contract removes the straight laser and conditional lasers.
 * - Conditional removes a contract laser, and the straight laser when there is no
 *   standing amount (Standard → Conditional keeps it as the standing deductible).
 * Saving the type the claimant already has (`origin`) keeps legacy extras, such
 * as conditional lasers under a contract laser: nothing is converted silently.
 */
export const laserClearPlan = ({
  origin,
  kind,
  existing,
  standingSet,
}: {
  origin?: LaserKind;
  kind: LaserKind;
  existing: ExistingLaser;
  /** Conditional only: a standing amount is entered. */
  standingSet: boolean;
}): LaserClearPlan => {
  const changed = origin !== kind;
  const has = {
    straight: existing.straightAmount !== undefined,
    conditions: existing.conditions.length > 0,
    contract: !!existing.contract,
  };
  switch (kind) {
    case "standard":
      return { straight: false, conditions: changed && has.conditions, contract: changed && has.contract };
    case "contract":
      return { straight: changed && has.straight, conditions: changed && has.conditions, contract: false };
    case "conditional":
      return { straight: has.straight && !standingSet, conditions: false, contract: changed && has.contract };
  }
};

const wholeUSD = (n?: number) =>
  n === undefined ? "" : formatUSD(n, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/** "a, b and c" */
export const joinAnd = (items: string[]): string =>
  items.length <= 1 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

/** "2 conditional lasers (ESRD, CKD)" */
export const conditionListText = (conditions: Pick<LaseredCondition, "lasered_condition_name">[]): string => {
  const names = conditions.map((c) => c.lasered_condition_name).join(", ");
  const n = conditions.length;
  return `${n} conditional laser${n === 1 ? "" : "s"}${names ? ` (${names})` : ""}`;
};

/**
 * "The straight laser ($150,000) and 2 conditional lasers (ESRD, CKD) will be
 * removed." for the save confirm; "" when the plan removes nothing.
 */
export const describeLaserClear = (plan: LaserClearPlan, existing: ExistingLaser): string => {
  const items: string[] = [];
  if (plan.straight) items.push(`the straight laser (${wholeUSD(existing.straightAmount)})`);
  if (plan.conditions && existing.conditions.length) items.push(conditionListText(existing.conditions));
  if (plan.contract && existing.contract) {
    const amt = existing.contract.amt_contract_lasered_deductible;
    items.push(
      existing.contract.is_contract_excluded
        ? "the contract exclusion"
        : `the contract laser${amt ? ` (${wholeUSD(amt)})` : ""}`
    );
  }
  if (items.length === 0) return "";
  const list = joinAnd(items);
  return `${list[0].toUpperCase()}${list.slice(1)} will be removed.`;
};
